import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useSafeExecute } from '../safeExecute';
import { useAuth } from '../../../session/InternalAuthProvider';
import { AccountData, CustomErrorCode, EmployeeAssignmentExceptionResponse } from '../../../api';
import { getDateFormat, getTimeFormat } from '../../getDateAndTimeFormat';
import { useLanguage } from '../../GeneralUtils';
import { getOrderPages } from '../../AccountUtils';

export const useEmployeeConflictHandler = () => {
  const executeSafely = useSafeExecute();
  const { t } = useTranslation();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { shortDateFormat } = getDateFormat(account);
  const language = useLanguage();

  const executeWithConflictCheck = async <T,>(
    operation: () => Promise<T>,
    successMessage: string,
    finallyCallback?: () => void,
  ): Promise<T> => {
    return executeSafely({
      apiCall: operation,
      successMessage,
      onErrorMessageGenerator: (error) => {
        if (error.body.error_code === CustomErrorCode.EMPLOYEE_ASSIGNMENT_EXCEPTION) {
          const orderPages = getOrderPages(account);
          const errorResponse = error.body as EmployeeAssignmentExceptionResponse;
          return t('calendar.edit_event.error_employee_assignment', {
            conflictingDate: moment(errorResponse.conflicting_date).format(shortDateFormat),
            conflictingTime: moment(errorResponse.conflicting_time).format(getTimeFormat(account)),
            orderPage: orderPages?.find((x) => x.id === errorResponse.order_page_id)?.label[language],
            orderNumber: errorResponse.order_number,
          });
        }
        return t('calendar.edit_event.error');
      },
      finallyCallback,
    });
  };

  return { executeWithConflictCheck };
};
